import React, { useState, useEffect, useRef } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import {
  FiCamera,
  FiCameraOff,
  FiRefreshCw,
  FiCheckCircle,
} from "react-icons/fi";
import { MdQrCodeScanner } from "react-icons/md";

const BarcodeTest = () => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(false);
  const videoRef = useRef(null);
  const controlsRef = useRef(null);

  useEffect(() => {
    if (!scanning) return;

    // 1. Tell the decoder exactly which formats to look for (faster than scanning all)
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.QR_CODE,
      BarcodeFormat.DATA_MATRIX,
    ]);
    // 2. tryHarder = spend more CPU to decode difficult/angled barcodes
    hints.set(DecodeHintType.TRY_HARDER, true);

    const codeReader = new BrowserMultiFormatReader(hints, {
      delayBetweenScanAttempts: 100, // scan every 100ms instead of default 500ms
    });

    // 3. Request the back camera at full HD resolution with continuous autofocus
    const constraints = {
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        focusMode: { ideal: "continuous" },
      },
    };

    codeReader
      .decodeFromConstraints(constraints, videoRef.current, (res, err) => {
        if (res) {
          setResult(res.getText());
          setFlash(true);
          setScanning(false);
          setTimeout(() => setFlash(false), 600);
        }
        // Per-frame decode failures (NotFoundException, ChecksumException,
        // FormatException) are routine — never show them. Real camera failures
        // reject the promise and are handled in .catch() below.
        // Filtering on err.name does not work in a production build: Terser
        // mangles zxing's class names, so the comparison never matches there.
        if (err) console.debug("[scanner] decode attempt:", err?.message);
      })
      .then((controls) => {
        controlsRef.current = controls;
      })
      .catch((e) => {
        setError(e.message || "Failed to access camera");
        setScanning(false);
      });

    return () => {
      try {
        controlsRef.current?.stop();
      } catch (_) {}
    };
  }, [scanning]);

  const handleReset = () => {
    setResult(null);
    setError(null);
    setScanning(false);
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <MdQrCodeScanner style={styles.headerIcon} />
        <div>
          <h1 style={styles.title}>Barcode Scanner</h1>
          <p style={styles.subtitle}>
            Point your camera at a barcode or QR code
          </p>
        </div>
      </div>

      {/* Scanner Card */}
      <div style={styles.card}>
        {scanning ? (
          <div style={styles.scannerWrapper}>
            <div style={styles.scannerOverlay}>
              <video
                ref={videoRef}
                style={styles.video}
                autoPlay
                muted
                playsInline
              />
              {/* Corner brackets */}
              <div
                style={{
                  ...styles.corner,
                  top: 20,
                  left: 20,
                  borderTop: "3px solid #6C63FF",
                  borderLeft: "3px solid #6C63FF",
                }}
              />
              <div
                style={{
                  ...styles.corner,
                  top: 20,
                  right: 20,
                  borderTop: "3px solid #6C63FF",
                  borderRight: "3px solid #6C63FF",
                }}
              />
              <div
                style={{
                  ...styles.corner,
                  bottom: 20,
                  left: 20,
                  borderBottom: "3px solid #6C63FF",
                  borderLeft: "3px solid #6C63FF",
                }}
              />
              <div
                style={{
                  ...styles.corner,
                  bottom: 20,
                  right: 20,
                  borderBottom: "3px solid #6C63FF",
                  borderRight: "3px solid #6C63FF",
                }}
              />
              {/* Scan line */}
              <div style={styles.scanLine} />
            </div>

            <button
              style={styles.stopButton}
              onClick={() => setScanning(false)}
            >
              <FiCameraOff style={{ marginRight: 8 }} />
              Stop Scanner
            </button>
          </div>
        ) : (
          <div style={styles.idleWrapper}>
            {result ? (
              <div
                style={{
                  ...styles.resultBox,
                  boxShadow: flash ? "0 0 0 4px #6C63FF55" : "none",
                }}
              >
                <FiCheckCircle style={styles.resultIcon} />
                <p style={styles.resultLabel}>Scanned Result</p>
                <p style={styles.resultText}>{result}</p>
                <div style={styles.actionRow}>
                  <button
                    style={styles.scanAgainButton}
                    onClick={() => {
                      setError(null);
                      setScanning(true);
                    }}
                  >
                    <FiCamera style={{ marginRight: 8 }} />
                    Scan Again
                  </button>
                  <button style={styles.resetButton} onClick={handleReset}>
                    <FiRefreshCw style={{ marginRight: 8 }} />
                    Reset
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.placeholder}>
                <div style={styles.placeholderIcon}>
                  <MdQrCodeScanner size={80} color="#6C63FF" />
                </div>
                <p style={styles.placeholderText}>No barcode scanned yet</p>
                <p style={styles.placeholderHint}>
                  Tap the button below to start
                </p>
              </div>
            )}

            {!result && (
              <button
                style={styles.startButton}
                onClick={() => {
                  setError(null);
                  setScanning(true);
                }}
              >
                <FiCamera style={{ marginRight: 8 }} />
                Start Scanning
              </button>
            )}
          </div>
        )}

        {error && (
          <div style={styles.errorBanner}>
            <span>⚠️ {error}</span>
          </div>
        )}
      </div>

      <p style={styles.tip}>
        Supports QR codes, EAN-13, EAN-8, UPC, Code 128, and more
      </p>

      <style>{`
        @keyframes scanMove {
          0%   { top: 15%; }
          50%  { top: 80%; }
          100% { top: 15%; }
        }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    fontFamily: "'Segoe UI', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 32,
    color: "#fff",
  },
  headerIcon: {
    fontSize: 48,
    color: "#6C63FF",
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: 0.5,
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 14,
    color: "#a0aec0",
  },
  card: {
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(16px)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 24,
    width: "100%",
    maxWidth: 420,
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
  },
  scannerWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  scannerOverlay: {
    position: "relative",
    width: "100%",
    aspectRatio: "1 / 1",
    overflow: "hidden",
    background: "#000",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  corner: {
    position: "absolute",
    width: 28,
    height: 28,
    zIndex: 10,
    borderRadius: 2,
  },
  scanLine: {
    position: "absolute",
    left: "10%",
    width: "80%",
    height: 2,
    background: "linear-gradient(90deg, transparent, #6C63FF, transparent)",
    zIndex: 10,
    animation: "scanMove 2s ease-in-out infinite",
    boxShadow: "0 0 8px #6C63FF",
  },
  stopButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "16px",
    padding: "12px 28px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 50,
    color: "#fff",
    fontSize: 15,
    cursor: "pointer",
  },
  idleWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "32px 24px",
  },
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 28,
  },
  placeholderIcon: {
    width: 140,
    height: 140,
    background: "rgba(108,99,255,0.1)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
    border: "2px dashed rgba(108,99,255,0.4)",
  },
  placeholderText: {
    color: "#e2e8f0",
    fontSize: 18,
    fontWeight: 600,
    margin: "0 0 6px",
  },
  placeholderHint: {
    color: "#718096",
    fontSize: 13,
    margin: 0,
  },
  startButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 40px",
    background: "linear-gradient(135deg, #6C63FF, #3ecfcf)",
    border: "none",
    borderRadius: 50,
    color: "#fff",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 8px 24px rgba(108,99,255,0.4)",
  },
  resultBox: {
    width: "100%",
    background: "rgba(108,99,255,0.12)",
    border: "1px solid rgba(108,99,255,0.35)",
    borderRadius: 16,
    padding: "24px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 20,
    transition: "box-shadow 0.3s",
  },
  resultIcon: {
    fontSize: 36,
    color: "#48bb78",
    marginBottom: 10,
  },
  resultLabel: {
    color: "#a0aec0",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 1,
    margin: "0 0 8px",
  },
  resultText: {
    color: "#fff",
    fontSize: 20,
    fontWeight: 700,
    wordBreak: "break-all",
    textAlign: "center",
    margin: "0 0 20px",
  },
  actionRow: {
    display: "flex",
    gap: 12,
  },
  scanAgainButton: {
    display: "flex",
    alignItems: "center",
    padding: "10px 20px",
    background: "linear-gradient(135deg, #6C63FF, #3ecfcf)",
    border: "none",
    borderRadius: 50,
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  resetButton: {
    display: "flex",
    alignItems: "center",
    padding: "10px 20px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 50,
    color: "#e2e8f0",
    fontSize: 14,
    cursor: "pointer",
  },
  errorBanner: {
    margin: "0 16px 16px",
    padding: "12px 16px",
    background: "rgba(229,62,62,0.15)",
    border: "1px solid rgba(229,62,62,0.4)",
    borderRadius: 10,
    color: "#fc8181",
    fontSize: 13,
    textAlign: "center",
  },
  tip: {
    marginTop: 20,
    color: "#4a5568",
    fontSize: 12,
    textAlign: "center",
  },
};

export default BarcodeTest;
